import React from "react";

/*
 * Brand-isolated loader for the Polaris × COD Full Throttle hub. Shown while:
 *   1. useGetHub("polaris-cod-fullthrottle") resolves
 *   2. The FullThrottleHub lazy chunk downloads
 *
 * Inline styles only — must render before the hub's SCSS is parsed by the
 * browser. Mirrors the MinelabRttrLoadingFallback pattern (brand isolation
 * contract: no platform-themed skeleton ever flashes for a partner-branded
 * URL). Polaris orange + Hitmarker-stand-in display weight match the
 * wireframe theme tokens.
 */
export const FullThrottleLoadingFallback: React.FC = () =>
    <div style={loaderStyles.root}>
        <div style={loaderStyles.label}>FULL THROTTLE CHALLENGE</div>
        <div style={loaderStyles.bar}>
            <div style={loaderStyles.barInner} />
        </div>
    </div>;

const loaderStyles: Record<string, React.CSSProperties> = {
    root: {
        position: "fixed",
        inset: 0,
        background: "#0a0a0a",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 18,
        zIndex: 9999,
    },
    label: {
        fontFamily: "\"Hitmarker Condensed\", \"Oswald\", Impact, sans-serif",
        fontWeight: 700,
        fontSize: "clamp(20px, 4vw, 32px)",
        letterSpacing: "0.04em",
        color: "#ff7a00",
        textTransform: "uppercase",
        textShadow: "0 2px 8px rgba(255,122,0,0.35)",
    },
    bar: {
        width: "min(280px, 60vw)",
        height: 3,
        background: "rgba(255,255,255,0.08)",
        borderRadius: 2,
        overflow: "hidden",
        position: "relative",
    },
    barInner: {
        position: "absolute",
        top: 0,
        bottom: 0,
        left: 0,
        width: "30%",
        background: "linear-gradient(90deg, transparent, #ff7a00, transparent)",
        animation: "fullthrottle-loader-slide 1.2s ease-in-out infinite",
    },
};

/*
 * Keyframes injected lazily so we keep this component fully self-contained —
 * no SCSS import allowed during the brand-isolation window.
 */
if (typeof document !== "undefined") {
    const styleId = "fullthrottle-loader-keyframes";
    if (!document.getElementById(styleId)) {
        const styleEl = document.createElement("style");
        styleEl.id = styleId;
        styleEl.textContent = "@keyframes fullthrottle-loader-slide { 0% { left: -30%; } 100% { left: 100%; } }";
        document.head.appendChild(styleEl);
    }
}

export default FullThrottleLoadingFallback;
